import React from 'react';
import { Text } from 'library';
import { styled } from '@mui/material';

import { Link } from './Link';
import { Notification } from './Notification';

import type { NotificationType } from 'models';
import type { DashboardSummary } from '../types';
import { useUserContext } from 'UserContext';

const VPK_NOTIFICATION: NotificationType = {
    id: -1,
    message:
        'After utilizing a specific substitute for four weeks in a VPK program (or 60 program hours), please add them to your VPK program portal. You can track hours worked by Tandem substitutes via My Staff.',
    added_at: 'VPK Providers',
    image: null,
    clicked: false,
    viewed: false,
    marked_as_read: false,
    hide_mark_as_read: true,
    actions: [
        {
            name: 'View My Staff',
            action: 'navigate',
            action_type: 'web',
            path: '/providers',
            extra_data: null,
        },
    ],
};

export function Notifications({ data }: { data: DashboardSummary }) {
    const { user } = useUserContext();
    const isManateeCounty = user?.address?.some(
        (a) => a.county?.toLowerCase() === 'manatee' && a.state?.toLowerCase() === 'florida',
    );
    const showVPKNotification = isManateeCounty;
    const extraNotifications = showVPKNotification ? 1 : 0;
    const unreads: NotificationType[] | undefined = data?.notifications?.unread;
    const unreadCount: number = (data?.notifications?.unread_count || 0) + extraNotifications;
    const max = data?.jobs?.drafts?.length ? unreads?.length : 2;
    return (
        <CardBody>
            <Inner>
                <Row>
                    <Text variant="h1" bold>
                        Latest Notifications
                    </Text>
                    {!!unreadCount && (
                        <Text variant="caption">
                            {max}/{unreadCount}
                        </Text>
                    )}
                </Row>
                {showVPKNotification && (
                    <NotificationBody>
                        <Notification notification={VPK_NOTIFICATION} />
                    </NotificationBody>
                )}
                <NotificationBody>
                    {!unreadCount ? (
                        <Text variant="body2">You&apos;re all caught up! 🎉</Text>
                    ) : (
                        unreads.map(
                            (notification: NotificationType, index: number) =>
                                index < max && <Notification key={notification.id} notification={notification} />,
                        )
                    )}
                </NotificationBody>
            </Inner>
            <BottomRow>{!!unreadCount && <Link to="/notifications">View All</Link>}</BottomRow>
        </CardBody>
    );
}

const CardBody = styled('div')({
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
    width: '100%',
});

const Inner = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
});

const Row = styled('div')({
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
});

const NotificationBody = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '10px 4px',
    width: '100%',
});

const BottomRow = styled('div')({
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
});
